import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Alert, Dialog, DialogContent, Loading } from '@guildeducationinc/recess';
import FileCopyIcon from '@guildeducationinc/recess/icons/FileCopyIcon';
import FileDownloadOutlinedIcon from '@guildeducationinc/recess/icons/FileDownloadOutlinedIcon';
import { DOWNLOAD_EXPORT } from '@src/graphql/queries/exportRun';
import { useExportRuns } from '@src/hooks/useExportRuns';
import { ExportStatus } from '@src/types';
import { DataTable, TrackableButton, useLocale } from 'pe-core-frontend';
import { useState } from 'react';
export const ExportRuns = ({ clientId }) => {
    const { t, formatToLocalDatetime } = useLocale();
    const [exportError, setExportError] = useState(undefined);
    const { exportRuns, loading, error, page, pageSize, setPage, setPageSize, orderBy, setOrderBy, orderByDir, setOrderByDir, total, } = useExportRuns({
        clientId,
    });
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleShowErrors = (row) => {
        setExportError(row.error);
    };
    const [downloadExportQuery] = useLazyQuery(DOWNLOAD_EXPORT);
    const handleDownloadExport = async (id) => {
        const response = await downloadExportQuery({ variables: { id } });
        if (response.data?.downloadExport) {
            window.open(response.data?.downloadExport);
        }
    };
    return (_jsxs(Loading, { label: 'ExportRuns', loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: exportRuns || [], pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                    setPage(pagination.page + 1);
                    setPageSize(pagination.pageSize);
                }, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'id', sort: orderByDir }], columns: [
                    {
                        field: 'exportName',
                        headerName: t('Export Name'),
                        flex: 0.2,
                        sortable: true,
                    },
                    {
                        field: 'status',
                        headerName: t('Status'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => t(`ExportStatus.${value}`),
                    },
                    {
                        field: 'numRowsExported',
                        headerName: t('Exported'),
                        flex: 0.1,
                        sortable: true,
                    },
                    {
                        field: 'totalRows',
                        headerName: t('Total'),
                        flex: 0.1,
                        sortable: true,
                    },
                    {
                        field: 'startedAt',
                        headerName: t('Started'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                    },
                    {
                        field: 'completedAt',
                        headerName: t('Completed'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                    },
                    {
                        field: 'action',
                        headerName: t('Actions'),
                        renderCell: ({ row }) => (_jsxs("div", { children: [row.error && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Show errors', tag: `export_run_${row.id}`, label: t('Show errors'), appearance: 'minimal', icon: _jsx(FileCopyIcon, { size: 20 }), onClick: () => handleShowErrors(row), className: 'margin--right_8' })), (row.status === ExportStatus.COMPLETED ||
                                    row.status === ExportStatus.COMPLETED_WITH_ERRORS) && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Download', tag: `export_run_${row.id}`, label: t('Download'), appearance: 'minimal', icon: _jsx(FileDownloadOutlinedIcon, { size: 20 }), onClick: () => handleDownloadExport(row.id), className: 'margin--right_8' }))] })),
                        width: 100,
                        sortable: false,
                    },
                ] }), _jsx(Dialog, { isOpen: exportError !== undefined, title: t('Export Errors'), onClose: () => setExportError(undefined), size: 'xl', children: _jsx(DialogContent, { children: _jsx("pre", { className: 'text-sm border border-1 border-gray-700 bg-gray-300 p-3 overflow-auto', children: exportError }) }) })] }));
};
